import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Plus, Trash2, ArrowLeft, GripVertical, ImagePlus, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TRIGGERS = [
  { value: "new_lead", label: "New Lead Added" },
  { value: "stage_change", label: "Lead Stage Change" },
  { value: "tag_applied", label: "Tag Applied" },
  { value: "specific_date", label: "Specific Date" },
  { value: "manual", label: "Manual Enrollment" },
];

const DELAYS = [
  { hours: 0, label: "Immediately" },
  { hours: 1, label: "After 1 hour" },
  { hours: 6, label: "After 6 hours" },
  { hours: 24, label: "After 1 day" },
  { hours: 48, label: "After 2 days" },
  { hours: 72, label: "After 3 days" },
  { hours: 168, label: "After 1 week" },
  { hours: 336, label: "After 2 weeks" },
  { hours: 720, label: "After 1 month" },
];

const MERGE_FIELDS = ["{first_name}", "{last_name}", "{agent_name}", "{address}"];

function MessageStep({ index, msg, onChange, onDelete, total }) {
  return (
    <div className="card p-5 relative" data-testid={`message-step-${index}`}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col items-center gap-1 pt-1">
          <div className="w-7 h-7 rounded-full bg-[#FF5C35] text-white text-xs font-semibold flex items-center justify-center shrink-0">
            {index + 1}
          </div>
          {index < total - 1 && <div className="w-0.5 h-6 bg-slate-200" />}
        </div>
        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Send Timing</label>
              <select
                value={msg.delay_hours}
                onChange={e => onChange(index, "delay_hours", parseInt(e.target.value))}
                className="input w-auto text-sm"
                data-testid={`delay-select-${index}`}
              >
                {DELAYS.map(d => <option key={d.hours} value={d.hours}>{d.label}</option>)}
              </select>
            </div>
            <button onClick={() => onDelete(index)} className="text-slate-400 hover:text-red-500 transition-colors mt-5" data-testid={`delete-step-${index}`}>
              <Trash2 size={15} />
            </button>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-slate-600">Message</label>
              <div className="flex gap-1">
                {MERGE_FIELDS.map(f => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => onChange(index, "body", (msg.body || "") + f)}
                    className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded hover:bg-[#FFF0EB] hover:text-[#E84E29] transition-colors"
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              value={msg.body}
              onChange={e => onChange(index, "body", e.target.value)}
              className="input resize-none text-sm"
              rows={3}
              placeholder="Write your message..."
              data-testid={`message-body-${index}`}
            />
            <p className="text-xs text-slate-400 mt-1">{msg.body?.length || 0} chars</p>
          </div>
          {/* MMS Image Attach */}
          <StepImageAttach
            mediaUrl={msg.media_url || ""}
            onChange={(url) => onChange(index, "media_url", url)}
          />
        </div>
      </div>
    </div>
  );
}

function StepImageAttach({ mediaUrl, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowed.includes(file.type)) { alert("Only JPEG, PNG, GIF, or WebP allowed"); return; }
    if (file.size > 5 * 1024 * 1024) { alert("Image must be under 5 MB"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/api/upload/image`, form);
      onChange(r.data.url);
    } catch (e) { alert(e.response?.data?.detail || "Upload failed"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="mt-2">
      <input type="file" ref={fileRef} accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleFile} />
      {mediaUrl ? (
        <div className="flex items-center gap-2 bg-slate-50 rounded-md p-2">
          <img src={mediaUrl} alt="" className="w-12 h-12 object-cover rounded" onError={e => { e.target.style.display = "none"; }} />
          <span className="text-xs text-slate-600 flex-1 truncate">MMS image attached</span>
          <button onClick={() => onChange("")} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
        </div>
      ) : (
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-dashed border-slate-300 rounded-md text-slate-500 hover:bg-slate-50 hover:border-slate-400 transition-colors disabled:opacity-50"
        >
          {uploading ? <div className="w-3 h-3 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" /> : <ImagePlus size={13} />}
          {uploading ? "Uploading..." : "Attach Photo (MMS)"}
        </button>
      )}
    </div>
  );
}

export default function CampaignEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState("manual");
  const [triggerValue, setTriggerValue] = useState("");
  const [messages, setMessages] = useState([{ delay_hours: 0, body: "" }]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (isEdit) {
      axios.get(`${API}/api/campaigns`)
        .then(r => {
          const c = r.data.find(x => x.id === id);
          if (c) {
            setName(c.name);
            setTrigger(c.trigger);
            setTriggerValue(c.trigger_value || "");
            setMessages(c.messages && c.messages.length > 0 ? c.messages : [{ delay_hours: 0, body: "" }]);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const updateMessage = (i, field, value) => {
    setMessages(msgs => msgs.map((m, idx) => idx === i ? { ...m, [field]: value } : m));
  };

  const addStep = () => setMessages(msgs => [...msgs, { delay_hours: 24, body: "" }]);
  const deleteStep = (i) => setMessages(msgs => msgs.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) { alert("Campaign name is required"); return; }
    if (messages.some(m => !m.body.trim())) { alert("All message steps need content"); return; }
    setSaving(true);
    try {
      const payload = { name, trigger, trigger_value: triggerValue, messages };
      if (isEdit) {
        await axios.put(`${API}/api/campaigns/${id}`, payload);
      } else {
        await axios.post(`${API}/api/campaigns`, payload);
      }
      navigate("/campaigns");
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 md:p-8 max-w-3xl" data-testid="campaign-editor-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/campaigns" className="text-slate-400 hover:text-slate-600" data-testid="back-to-campaigns">
          <ArrowLeft size={18} />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold font-heading text-slate-900">{isEdit ? "Edit Campaign" : "New Campaign"}</h1>
          <p className="text-slate-500 text-sm">Configure your automated SMS sequence</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Campaign Details */}
        <div className="card p-6 space-y-4" data-testid="campaign-details">
          <h3 className="text-sm font-semibold text-slate-900 font-heading uppercase tracking-wide">Campaign Details</h3>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Campaign Name</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="e.g. New Lead Welcome Sequence" data-testid="campaign-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Trigger</label>
              <select value={trigger} onChange={e => setTrigger(e.target.value)} className="input" data-testid="trigger-select">
                {TRIGGERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            {(trigger === "tag_applied" || trigger === "stage_change") && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {trigger === "tag_applied" ? "Tag Name" : "Stage Name"}
                </label>
                <input value={triggerValue} onChange={e => setTriggerValue(e.target.value)} className="input" placeholder={trigger === "tag_applied" ? "e.g. open-house" : "e.g. Hot Lead"} data-testid="trigger-value-input" />
              </div>
            )}
          </div>
        </div>

        {/* Message Sequence */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900 font-heading uppercase tracking-wide">Message Sequence</h3>
            <button onClick={addStep} className="btn-secondary py-1.5 px-3 text-xs" data-testid="add-step-btn">
              <Plus size={13} /> Add Step
            </button>
          </div>
          <div className="space-y-3">
            {messages.map((msg, i) => (
              <MessageStep
                key={i}
                index={i}
                msg={msg}
                onChange={updateMessage}
                onDelete={deleteStep}
                total={messages.length}
              />
            ))}
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <Link to="/campaigns" className="btn-secondary" data-testid="cancel-btn">Cancel</Link>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-campaign-btn">
            {saving ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</span> : (isEdit ? "Save Changes" : "Create Campaign")}
          </button>
        </div>
      </div>
    </div>
  );
}
